import { useEffect, useState } from "react";
import { getData } from "../../Services/getData";
import { deleteData } from "../../Services/deleteData";
import { endpoints } from "../../Utils/UrlEnum";
import { Vocabulary } from "../../Utils/Vocabulary";
import styles from "./ExamsCalendar.module.css";
import useCustomMemo from "../../Hooks/useCustomMemo";

export interface ExamEvent {
  id: number;
  text: string;
  start: string;
  end: string;
  barColor?: string;
  examType?: string;
  clientName?: string;
  allDay?: boolean;
  workPointId?: string;
}

export type ViewType = "Week" | "Month" | "Year";

export const useExamsCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewType, setViewType] = useState<ViewType>("Month");
  const [events, setEvents] = useState<ExamEvent[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ExamEvent | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedWorkPointId, setSelectedWorkPointId] = useState<string>("");
  const memo = useCustomMemo();
  const workPoints = memo?.storage?.workPoints || [];

  // Handle work point filter change
  const handleWorkPointChange = (workPointId: string) => {
    setSelectedWorkPointId(workPointId);
  };

  useEffect(() => {
    fetchExamEvents();
  }, [selectedWorkPointId]);

  const fetchExamEvents = async () => {
    try {
      const response = await getData(
        `${
          endpoints.exams.certificateExams
        }?workPointId=${selectedWorkPointId}&startDate=${currentDate.toISOString()}`
      );
      const examData = response.data;

      const formattedEvents: ExamEvent[] = examData.map((exam: any) => {
        const startDate = new Date(exam.examDate);
        const endDate = new Date(startDate.getTime() + 3 * 60 * 60 * 1000); // Add 8 hours
        const workPoint = workPoints?.find(
          (wp: any) => wp._id === exam.workPointId
        );

        return {
          id: exam._id || exam.id,
          text: Vocabulary.scheduledExam + (workPoint ? ` - ${workPoint.name}` : ""),
          start: startDate.toISOString(),
          end: endDate.toISOString(),
          barColor: "#1ab394",
          allDay: false, // Changed to false to show as timed event
          cssClass: styles.examEvent,
          workPointId: exam.workPointId,
        };
      });

      setEvents(formattedEvents);
    } catch (error) {
      console.error("Error fetching exam events:", error);
      // Keep empty events array on error
      setEvents([]);
    }
  };

  useEffect(() => {
    fetchExamEvents();
  }, []);

  const onEventClick = async (args: any) => {
    // Check if the click was on the delete button
    if (args.originalEvent && args.originalEvent.target && 
        (args.originalEvent.target.classList.contains('exam-delete-btn') || 
         args.originalEvent.target.closest('.exam-delete-btn'))) {
      // Don't open modal for delete button clicks
      return;
    }
    
    const eventData = events.find((e) => e.id === args.e.id());
    if (eventData) {
      setSelectedEvent(eventData);
      setOpenModal(true);
    }
  };

  const onTimeRangeSelect = async (args: any) => {
    setSelectedDate(args.start);
    setSelectedEvent(null);
    setOpenModal(true);
  };


  const handleViewChange = (_: any, newView: ViewType) => {
    if (newView !== null) {
      setViewType(newView);
    }
  };

  const navigateDate = (direction: "prev" | "next") => {
    const newDate = new Date(currentDate);
    if (viewType === "Week") {
      newDate.setDate(currentDate.getDate() + (direction === "next" ? 7 : -7));
    } else if (viewType === "Month") {
      newDate.setMonth(
        currentDate.getMonth() + (direction === "next" ? 1 : -1)
      );
    } else if (viewType === "Year") {
      newDate.setFullYear(
        currentDate.getFullYear() + (direction === "next" ? 1 : -1)
      );
    }
    setCurrentDate(newDate);
  };

  const getDateDisplay = () => {
    if (viewType === "Week") {
      const startOfWeek = new Date(currentDate);
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
      startOfWeek.setDate(diff);

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);

      return `${startOfWeek.getDate()} - ${endOfWeek.getDate()} ${endOfWeek.toLocaleDateString(
        "ro-RO",
        { month: "long", year: "numeric" }
      )}`;
    } else if (viewType === "Month") {
      return currentDate.toLocaleDateString("ro-RO", {
        month: "long",
        year: "numeric",
      });
    } else {
      return currentDate.getFullYear().toString();
    }
  };

  const weekConfig = {
    viewType: "Week" as const,
    locale: "ro-ro",
    headerHeight: 40,
    cellHeight: 25,
    dayBeginsHour: 8,
    dayEndsHour: 18,
    businessBeginsHour: 8,
    businessEndsHour: 18,
    showNonBusiness: false, // hide non-business hours
    timeRangeSelectingStartEndEnabled: true,
    eventMoveHandling: "Update" as const,
    eventResizeHandling: "Update" as const,
    onEventClicked: onEventClick,
    onTimeRangeSelected: onTimeRangeSelect,
    showAllDayEvents: true,
    allDayEventsHeight: 40,
    headerDateFormat: "dddd, dd MMMM yyyy",
    heightSpec: "BusinessHoursNoScroll" as const, // fit business hours, no scroll
    hourNameBackColor: "black",
    hideFreeBusy: true,
    weekStarts: 1, // Monday
  };

  const monthConfig = {
    locale: "ro-ro",
    cellHeight: 100,
    eventMoveHandling: "Update" as const,
    eventResizeHandling: "Update" as const,
    onEventClicked: onEventClick,
    onTimeRangeSelected: onTimeRangeSelect,
    showWeekend: true,
    eventHeight: 100, // fill the cell height
  };

  const deleteExam = async (examId: string) => {
    try {
      await deleteData(`${endpoints.exams.certificateExams}/${examId}`);
      await fetchExamEvents();
    } catch (error) {
      console.error("Error deleting exam:", error);
      throw error;
    }
  };

  return {
    currentDate,
    setCurrentDate,
    viewType,
    setViewType,
    events,
    openModal,
    setOpenModal,
    selectedEvent,
    setSelectedEvent,
    onEventClick,
    onTimeRangeSelect,
    handleViewChange,
    navigateDate,
    getDateDisplay,
    weekConfig,
    monthConfig,
    refreshEvents: fetchExamEvents,
    selectedDate,
    setSelectedDate,
    selectedWorkPointId,
    handleWorkPointChange,
    deleteExam,
  };
};
